import React from 'react';
import ItemList from '../../../components/item-list/ItemList';

const PaymentsReceivableTable = ({
  payments,
  selectedPayments,
  onSelectAll,
  onSelectPayment,
  onToggleExpand,
  onDeleteSelected,
  onEdit,
  onViewFiles,
  onCreateInvoice,
  onRecordPayment,
  canSelect = true,
  canDelete = true,
  canEdit = true,
  currentPage,
  onPageChange,
  totalItems,
  itemsPerPage
}) => {
  const columns = [
    { key: 'orderId', label: 'Order ID' },
    { key: 'customerRate', label: 'Customer Rate' },
    { key: 'totalAmount', label: 'Total Amount' },
    { key: 'status', label: 'Status' },
    { key: 'daysOnHold', label: 'Days Waiting' },
    { key: 'pastDue', label: 'Past DUE' },
    { key: 'actions', label: 'Actions' }
  ];

  // Helper function to extract number from paymentTerms string
  const extractPaymentTermsDays = (paymentTerms) => {
    if (!paymentTerms) return null;
    
    // Try to extract number from string (e.g., "30", "30 days", "Net 30", "Payment due within 30 days")
    const match = paymentTerms.toString().match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  };

  // Transform payments data for ItemList
  const transformedPayments = payments.map(payment => {
    const load = payment.load || {};
    const customer = payment.customer || {};
    
    // Days on Hold: from server data (counter starts on on Hold/received, stops on payed)
    const daysOnHoldValue = payment.daysOnHold !== null && payment.daysOnHold !== undefined
      ? `${payment.daysOnHold} ${payment.daysOnHold === 1 ? 'day' : 'days'}`
      : '—';
    
    // Calculate past DUE
    const daysOnHoldRaw = payment.daysOnHold;
    const paymentTermsDays = extractPaymentTermsDays(customer.paymentTerms);
    const isPastDue = daysOnHoldRaw !== null && 
                      daysOnHoldRaw !== undefined && 
                      paymentTermsDays !== null && 
                      daysOnHoldRaw > paymentTermsDays;
    
    const pastDueValue = isPastDue 
      ? `${daysOnHoldRaw - paymentTermsDays} ${(daysOnHoldRaw - paymentTermsDays) === 1 ? 'day' : 'days'}`
      : null;
    
    const deadlineDays = paymentTermsDays ?? payment.deadlineDays;
    
    return {
      ...payment,
      // Order ID from load.orderId
      orderId: payment.orderId || 'N/A',
      
      // Original Customer Rate (not total)
      customerRate: load.customerRate ?? payment.customerRate ?? null,
      
      // Status from server
      status: payment.status || 'pending',
      
      // Days on Hold - counter from server
      daysOnHold: daysOnHoldValue,
      daysOnHoldRaw: payment.daysOnHold,
      
      // Past DUE - calculated on frontend
      pastDue: pastDueValue,
      isPastDue: isPastDue,
      paymentTermsDays: paymentTermsDays,
      
      // For badge display
      statusBadge: payment.status || 'pending',
      deadlineDays: deadlineDays,
      
      // Total Amount (server if available, otherwise calculate)
      totalAmount: (() => {
        if (payment.totalAmount !== null && payment.totalAmount !== undefined) {
          return payment.totalAmount;
        }
        const baseRate = parseFloat(load.customerRate ?? payment.customerRate ?? 0) || 0;
        const fees = Array.isArray(payment.fees) ? payment.fees : [];
        const feesTotal = fees.reduce((sum, fee) => {
          return sum + (parseFloat(fee.customerRate || fee.total || 0) || 0);
        }, 0);
        const tonuAmount = (payment.tonu?.enabled && payment.tonu?.customerRate)
          ? parseFloat(payment.tonu.customerRate) || 0
          : 0;
        return baseRate + feesTotal + tonuAmount;
      })(),

      // Keep original data for expanded view
      customerName: customer.companyName || 'N/A',
      // Ensure customer data is fully preserved with all fields
      customer: {
        ...customer,
        _id: customer._id || customer.id,
        id: customer._id || customer.id,
        companyName: customer.companyName,
        email: customer.email || null,
        phoneNumber: customer.phoneNumber,
        customerAddress: customer.customerAddress || customer.address,
        paymentMethod: customer.paymentMethod,
        paymentTerms: customer.paymentTerms,
        creditLimit: customer.creditLimit
      },
      load: load
    };
  });

  return (
    <ItemList
      items={transformedPayments}
      itemType="paymentsReceivable"
      selectedItems={selectedPayments}
      onSelectAll={onSelectAll}
      onSelectItem={onSelectPayment}
      onToggleExpand={onToggleExpand}
      onDeleteSelected={onDeleteSelected}
      onEdit={onEdit}
      onViewFiles={onViewFiles}
      canSelect={canSelect}
      canDelete={canDelete}
      canEdit={canEdit}
      onCreateInvoice={onCreateInvoice}
      onRecordPayment={onRecordPayment}
      currentPage={currentPage}
      onPageChange={onPageChange}
      totalItems={totalItems}
      itemsPerPage={itemsPerPage}
      columns={columns}
      isServerPaginated={true}
    />
  );
};

export default PaymentsReceivableTable;
